'use client';
import { useSmartIntakeCtx, resetCity, resolvePageCity } from '@/lib/smartIntakeStore';
import { useState, useEffect, useMemo, useRef } from 'react';
import { type ProAvailInput } from '@/lib/availability';
import CategoryProCard from './CategoryProCard';
import TopicProCard, { TOPIC_CARD_CSS } from './TopicProCard';
import TopicList3, { type List3Variant } from './TopicList3';
import ProSortTabs, { sortPros, PRO_SORT_DEFAULT, PRO_SORT_MIN, type ProSortKey } from './ProSortTabs';
import type { TopicCardVariant } from '@/lib/topicLayout';
import ProViewBeacon from './ProViewBeacon';
import { useRouter } from 'next/navigation';
import { prefetchFullIdle } from './IdlePrefetch';
import { SMART_INTAKE_KEY } from './SmartIntakeBar';
import { findCity } from '@/lib/israelCities';
import { cityPageOrder, CITY_PAGE_INITIAL, foundExpertsSegments, proCityDistanceNote, isSameCity } from '@/lib/proMatching';
import { logCityDemandGap } from '@/lib/logCityDemandGap';
import { cleanCity } from '@/lib/city';
import { CT2_SH_CSS, Ct2Heading } from '@/components/ct2/Ct2Rating';

type ReviewItem = { reviewer_name: string; reviewer_avatar: string | null; rating: number; review_text: string | null; review_date: string };

export type ExpertItem = {
  id: number; href: string; firstName: string; fullName: string; phone?: string;
  whatsappValid?: number | null;
  gender?: string | null; // he_professionals.gender → message prefill (heMsg proGender)
  imgSrc: string; rating10: number; reviewCount: number; // shown = raw Google 1-5 + Google's own total (Founder LAW 04/10/26)
  ratingRaw?: number; reviewCountOrder?: number; // stored values — ORDER inputs only (lib/proMatching), never shown
  categorySlug: string; categoryName: string; locationStr: string; proCity?: string | null;
  dealsClosed?: number; inquiriesReceived?: number; rankScore?: number; // rankScore = inquiries + promotion (lib/proRankScore) // track record — a tie-break inside the geo order (lib/proTrack.ts)
  lat: number | null; lng: number | null; reviews: ReviewItem[];
  serviceCities?: string | null; serviceRadiusKm?: number | null; // per-pro area override
  galleryTags?: string[][]; // IMAGE-SEO tag labels aligned with gallery
  gallery?: string[]; // work photos for the in-card carousel (category gallery_relevant=1)
  avail?: ProAvailInput; // per-pro availability set in the pro portal (2026-09-28)
};

// Default starting city (categories/pages with NO page-specific city — the base
// /{category} listing, not a /{category}/{city} one) before the user answers the
// questionnaire's city question — Tel Aviv, per product decision. Purely a local
// display/sort default: it never writes to smartIntakeCtx, so WaSmartButton's "pending
// city" typing preview (see feedback-experts-wa-preview-engine) is unaffected.
const FALLBACK_CITY = findCity('תל אביב');
let listHydrated = false; // true after the first mount in this tab — later mounts are client navigations (no SSR HTML to match)

// Renders the experts and filters them in real time by the city chosen in the questionnaire.
// A pro is relevant if the requested city is within his service radius (default 25km,
// see lib/israelCities.ts DEFAULT_RADIUS_KM — expands further only as a fallback, never
// silently, see lib/proMatching.ts).
export default function CategoryExpertsList({ experts, availStatus, presetCity, knownCity = null, nationwide = false, hideLocation = false, featuredId, categoryHref, cardVariant, compactCount = false, ct2 = false, ct2Head }: {
  ct2Head?: { plural: string; singular: string; tag: 'h1' | 'p' }; // ct2 heading "<n> <plural> בארץ|ב<city>" + city chip (replaces the H1 line, result line and "עיר אחרת")
  ct2?: boolean; // ?v=ct2 design variant: wide full-width WhatsApp/call row on each card
  // ?layout= preview only (Founder 01/10/26, lib/topicLayout.ts): compact card design + the ONE-line count. Omitted = unchanged.
  cardVariant?: TopicCardVariant;
  compactCount?: boolean;
  // Nationwide category: every expert serves every city — the list is never FILTERED by city, but it IS ordered by it (2026-09-28).
  nationwide?: boolean;
  // he_categories.location_relevant = 0 (Founder 11/10/26, lib/categoryLocation): the cards show no distance note.
  hideLocation?: boolean;
  experts: ExpertItem[]; availStatus: 'today' | 'tomorrow' | 'week';
  // 2026-08-19 bug fix (user report, /אינסטלטור/אשדוד showing pros far from Ashdod):
  // this page's OWN city (from the /{category}/{city} URL, threaded down from
  // CategoryPageBody) was never actually applied here — only FALLBACK_CITY (hardcoded
  // Tel Aviv) or a LATER questionnaire answer were. A visitor landing directly on a
  // city page (the common case — no questionnaire interaction yet) got silently
  // filtered/sorted around Tel Aviv while the page's own H1/URL said a different city,
  // with the status text even claiming "waiting for city selection" — the mismatch was
  // invisible. Now: a page-specific presetCity is treated as a REAL confirmed city from
  // the very first render (unless a later live questionnaire answer overrides it).
  presetCity?: { name: string; lat: number; lng: number } | null;
  // 30/09/26 (Founder: "loads, refreshes, loads OTHER experts"): the visitor's remembered city as the SERVER read it (xc_city
  // cookie, lib/knownCityCookie.ts) — /<category> is server-ranked for it, and this list starts from the very same name + coords,
  // so hydration never re-ranks. Replaces the Tel-Aviv default whenever the visitor has a city.
  knownCity?: { name: string; lat: number; lng: number } | null;
  categoryHref?: string; // '/<category>' — the city page's "עיר אחרת" goes here with the city cleared (2026-09-28)
  // PPC campaign mode (Founder 2026-09-23): this ONE expert renders normally; every other
  // expert in the category is still listed (links intact) but dimmed and marked as not in
  // this city. No radius filtering in this mode — the point is "him, and visibly not them".
  featuredId?: number | null;
}) {
  const initialCity = presetCity ?? knownCity ?? FALLBACK_CITY;
  const [cityName, setCityName] = useState<string | null>(initialCity?.name ?? null);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    initialCity ? { lat: initialCity.lat, lng: initialCity.lng } : null
  );

  // Shared smart-intake subscriber (one poller app-wide — see lib/smartIntakeStore)
  const intakeCtx = useSmartIntakeCtx();
  // A page-specific presetCity (this IS the page's declared city — /{cat}/{city}) counts
  // as a real confirmed answer, same as an actual questionnaire answer. Only the
  // Tel-Aviv FALLBACK_CITY (no page-specific city AND no questionnaire answer yet) is
  // the "not really confirmed" silent-default case the status text must not claim.
  // before the store is read (server render + hydration pass) the server's knownCity stands in for it — same answer both sides
  const hasCityAnswer = !!(presetCity || (intakeCtx ? intakeCtx.city : knownCity));
  // 2026-08-19 bug fix, take 2 (user report: on /אינסטלטור/נתניה the correct city flashes
  // for a moment, then flips to a WRONG city carried over from a different city page of
  // the same category visited earlier). A value-comparison guard ("does intakeCtx.city
  // differ from what I saw at my own first render?") turned out unreliable: useSmartIntakeCtx
  // hydrates asynchronously (getServerSnapshot is always null, so the very first render —
  // used to seed that comparison snapshot — never sees the real, already-stale localStorage
  // value; only a LATER render does, once useSyncExternalStore's subscription settles). That
  // made the genuinely stale cross-page value look "fresh" by comparison, which is exactly
  // the flash-then-wrong bug. Fixed with a real wall-clock guard instead of a value
  // comparison: QuestionnaireWizard now stamps `cityAnsweredAt` on every write that resolves
  // to a definitive city (a live answer OR this page's own presetCity — see savePartialCtx),
  // and proactively corrects the store to presetCity the moment it mounts. Here, only a
  // store value stamped AFTER this component's own mount is trusted — an old cross-page
  // write can never satisfy that, no matter how the hydration timing races.
  const mountedAtRef = useRef<number>(Date.now());
  useEffect(() => {
    const c = intakeCtx;
    // SAME rule as the questionnaire badge (re-verify N1 2026-09-28): on a page with its own URL city only an ACTIVE choice made
    // after this page opened moves the list — another tab merely loading a city page must not (cityChosenAt, not cityAnsweredAt)
    // ONE rule (lib/smartIntakeStore resolvePageCity) — the same city the badge and the WhatsApp button use
    const resolved = resolvePageCity(c) || presetCity?.name || null;
    const isFreshAnswer = !!resolved && (!presetCity || resolved !== presetCity.name);
    if (isFreshAnswer && resolved !== c?.city) {
      const f = findCity(resolved!); setCityName(resolved); if (f) setCoords({ lat: f.lat, lng: f.lng });
    } else if (isFreshAnswer) {
      setCityName(c!.city);
      // Precise coords from the questionnaire's Google autocomplete, if present —
      // otherwise the effect below resolves cityName via the dataset/geocode fallback.
      if (typeof c!.cityLat === 'number' && typeof c!.cityLng === 'number') setCoords({ lat: c!.cityLat, lng: c!.cityLng });
    } else {
      // No fresh LIVE answer on this page — fall back to this page's own preset city
      // if it has one, otherwise the generic Tel Aviv default. Never a stale carryover.
      const fallback = presetCity ?? (c ? null : knownCity) ?? FALLBACK_CITY;
      setCityName(fallback?.name ?? null);
      setCoords(fallback ? { lat: fallback.lat, lng: fallback.lng } : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeCtx, presetCity?.name]);

  // Resolve coordinates for the city (dataset → /api/geocode fallback)
  // Skipped on mount: the initial coords already belong to the initial city (server-ranked) — resolving them again here used to
  // overwrite precise coords with dataset ones for a frame (a re-rank flash). Runs on every LATER city change.
  const geoFirst = useRef(true);
  useEffect(() => {
    if (geoFirst.current) { geoFirst.current = false; if (cityName === initialCity?.name) return; }
    let cancelled = false;
    async function resolve() {
      if (!cityName) { setCoords(null); return; }
      const local = findCity(cityName);
      if (local) { if (!cancelled) setCoords({ lat: local.lat, lng: local.lng }); return; }
      try {
        const r = await fetch(`/api/geocode?city=${encodeURIComponent(cityName)}`);
        if (!r.ok) { if (!cancelled) setCoords(null); return; }
        const d = await r.json();
        if (!cancelled) setCoords(typeof d.lat === 'number' ? { lat: d.lat, lng: d.lng } : null);
      } catch { if (!cancelled) setCoords(null); }
    }
    resolve();
    return () => { cancelled = true; };
  }, [cityName]);

  // THE city-page order — ONE pipeline (lib/proMatching cityPageOrder), shared with the homepage strip (lib/homeExperts), so a
  // home sample's cards are exactly this page's top cards: declared-city pros first (campaign mode, the rest dimmed), else the
  // radius ladder; ranked same city → distance → rating (rankProsForCity); pros away until later go last.
  const { list: ranked, featuredIds, campaignMode, matchInfo } = useMemo(
    () => cityPageOrder(experts, cityName, coords, { nationwide, featuredId }),
    [experts, cityName, coords, nationwide, featuredId]);
  const shown = ranked;
  const inRadiusCount = matchInfo ? matchInfo.matched.length : experts.length;
  // Founder 2026-09-30: "מצאנו 11 מומחים ברמלה" was false — they SERVE Ramla, few LIVE there. ONE copy helper (lib/proMatching).
  const sameCityCount = matchInfo && cityName ? matchInfo.matched.filter(e => isSameCity(e.proCity ?? null, cityName)).length : 0;
  const foundLine = (city: string) => foundExpertsSegments(inRadiusCount, city, sameCityCount).map((s, i) => typeof s === 'number' ? <bdi key={i} dir="ltr">{s}</bdi> : <span key={i}>{s}</span>);
  // ct2 (1015 Nathan): every heading in the same pattern as the H1 "<category> · דירוג 4.5+ ★" — "<n> מומחים ב<city> · דירוג 4.5+ ★".
  const ct2Line = (city: string) => (
    <span className="ct2-sh">
      <span>{inRadiusCount === 1 ? 'מומחה אחד' : <><bdi dir="ltr">{inRadiusCount}</bdi> מומחים</>} ב{city}</span>
      <span className="ct2-sh-sep" aria-hidden="true">·</span>
      <span className="ct2-sh-rate"><span>דירוג <bdi dir="ltr">4.5+</bdi></span><span className="ct2-sh-star" aria-hidden="true">★</span></span>
    </span>
  );

  // 2026-08-19 (user request): "if I choose ראשון לציון it doesn't update those to be
  // shown first ... with some gentle effect". Re-sorting already happens automatically
  // (cityPageOrder above reacts to coords) — this just gives that reorder a
  // visible, gentle cue instead of an instant silent jump. Skipped on the very first
  // coords resolution (page load) so it never flashes on initial render, only on an
  // actual LATER city change.
  const [justResorted, setJustResorted] = useState(false);
  const isFirstCoords = useRef(true);
  useEffect(() => {
    if (isFirstCoords.current) { isFirstCoords.current = false; return; }
    // Hydration settling (Founder 11/10 flicker): the store/cookie sync right after mount may re-rank the list once — that is page
    // load, not a visitor action, so it must never dim the list (opacity pulse read as an empty flash). Pulse only for later changes.
    if (Date.now() - mountedAtRef.current < 2500) return;
    setJustResorted(true);
    const t = setTimeout(() => setJustResorted(false), 420);
    return () => clearTimeout(t);
  }, [coords?.lat, coords?.lng]);

  // Only log real user-picked cities, never the silent DEFAULT_CITY pre-sort probe.
  useEffect(() => {
    if (hasCityAnswer && cityName && matchInfo?.expanded) {
      logCityDemandGap(experts[0]?.categorySlug || '', cityName, matchInfo.radiusKm, matchInfo.expanded, matchInfo.matched.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCityAnswer, cityName, matchInfo]);
  const cityClean = cleanCity(cityName);
  // Founder 2026-09-27: more than four experts → show four, then a "הצגת עוד מומחים" button (only when there ARE more)
  const INITIAL = CITY_PAGE_INITIAL; // shared with the homepage strip (lib/homeExperts) — same cards, same count
  // Back from a pro page (client navigation) restores the list exactly as it was left — "הצגת עוד" included — so the browser's
  // scroll restoration lands on the same card. A first (hydrating) load always starts collapsed = the server HTML.
  const expKey = `xl-exp:${typeof location !== 'undefined' ? location.pathname : ''}`;
  const [expanded, setExpandedState] = useState<boolean>(() => {
    if (!listHydrated || typeof window === 'undefined') return false;
    try { return sessionStorage.getItem(expKey) === '1'; } catch { return false; }
  });
  const setExpanded = (v: boolean) => { setExpandedState(v); try { if (v) sessionStorage.setItem(expKey, '1'); else sessionStorage.removeItem(expKey); } catch {} };
  useEffect(() => { listHydrated = true; }, []);
  const cityFirst = useRef(true);
  useEffect(() => { if (cityFirst.current) { cityFirst.current = false; return; } setExpanded(false); }, [cityName]); // eslint-disable-line react-hooks/exhaustive-deps
  // M3 (Founder 02/10/26): ONE sort state for every design. Default 'near' = `ranked` exactly as produced (same array, never
  // re-sorted); 'reviews' = a stable reviewCount sort of the FULL list — sorted before the "show more" slice, so the expanded/
  // collapsed state survives a sort change and the first INITIAL are always the top of the chosen order.
  const [sortKey, setSortKey] = useState<ProSortKey>(PRO_SORT_DEFAULT);
  const sorted = useMemo(() => sortPros(ranked, sortKey), [ranked, sortKey]);
  // Founder 15/10/26: a chosen city (drawer/chip or city URL) shows ALL its experts at once — no "show more"; בארץ keeps the INITIAL cut
  const cityChosen = !nationwide && hasCityAnswer && !!cityClean;
  const visible = (expanded || cityChosen) ? sorted : sorted.slice(0, INITIAL);
  // "הצגת עוד" revealed cards → warm their pro pages too (the idle engine only takes the first few), so every tap is instant
  const router = useRouter();
  useEffect(() => { if (expanded) prefetchFullIdle(router, sorted.slice(INITIAL).map(e => e.href)); }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps
  const hidden = shown.length - visible.length;

  // ?layout= preview: ONE line — "17 מומחים זמינים לתל אביב" on the right, "החלף עיר" on the left (the same reset door as "עיר אחרת")
  const changeCity = () => { resetCity(); window.dispatchEvent(new Event('experts:open-bar')); if (presetCity && categoryHref) window.location.href = categoryHref; };
  const countN = (n: number) => n === 1 ? <><b className="font-black">מומחה אחד</b> זמין</> : <><b className="font-black"><bdi dir="ltr">{n}</bdi></b> מומחים זמינים</>;
  const compactLine = compactCount && !campaignMode && !nationwide ? (
    <div className="flex items-center justify-between gap-2 mb-2.5" data-count-line style={{ whiteSpace: 'nowrap' }}>
      <p className="min-w-0 truncate" style={{ fontSize: 15, color: '#0f172a' }}>
        {!hasCityAnswer ? countN(experts.length)
          : !coords ? `מאתר מומחים ב${cityClean}...`
          : matchInfo?.expanded && matchInfo.radiusKm == null ? `אין מומחים קרובים ל${cityClean} — כל המומחים בתחום`
          : <>{countN(inRadiusCount)} ל{cityClean}</>}
      </p>
      <button type="button" onClick={changeCity} className="flex-shrink-0 font-bold rounded-full relative"
        style={{ fontSize: 14, color: '#1d4ed8', border: '1.5px solid #bfdbfe', background: '#fff', padding: '3px 10px', cursor: 'pointer' }}>
        <span aria-hidden="true" style={{ position: 'absolute', inset: '-8px -2px' }} />{hasCityAnswer ? 'החלף עיר' : 'בחירת עיר'}
      </button>
    </div>
  ) : null;
  const L3: List3Variant[] = ['ledger', 'today', 'favorite', 'sort', 'sheet', 'letters'];
  const list3 = cardVariant && (L3 as string[]).includes(cardVariant) ? cardVariant as List3Variant : null;
  const compactCards = cardVariant && cardVariant !== 'full' && !list3 ? cardVariant as Exclude<TopicCardVariant, 'full' | List3Variant> : null;
  const cardList = compactCards ? (compactCards === 'tile' ? sorted : visible) : null;
  // the sort control: ≥3 experts, never in PPC campaign mode (there the featured pro must stay first)
  const sortBar = !campaignMode && ranked.length >= PRO_SORT_MIN
    ? <ProSortTabs value={sortKey} onChange={setSortKey} count={ranked.length} categorySlug={experts[0]?.categorySlug} chips={ct2} />
    : null;

  if (list3) {
    const cityLabel = !nationwide && hasCityAnswer && cityClean ? cityClean : null;
    const status = cityLabel && !coords ? `מאתר מומחים ב${cityClean}...` : cityLabel && matchInfo?.expanded && matchInfo.radiusKm == null ? `אין מומחים קרובים ל${cityClean}` : null;
    return (
      <div style={{ opacity: justResorted ? 0.45 : 1, transition: 'opacity .42s ease' }}>
        <TopicList3 variant={list3} experts={visible} nationwide={nationwide} count={cityLabel ? inRadiusCount : experts.length} cityLabel={cityLabel} status={status}
          onChangeCity={changeCity} coords={coords} sortBar={sortBar} />
        {hidden > 0 && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button type="button" onClick={() => setExpanded(true)} style={{ minHeight: 44, padding: '0 22px', borderRadius: 999, border: 'none', boxShadow: 'inset 0 0 0 1px #0f172a', background: '#fff', color: '#0f172a', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>
              עוד <bdi dir="ltr">{hidden}</bdi> מומחים
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {compactLine}
      {/* Status / result — plain text, no frame */}
      {ct2 && ct2Head && !compactLine && (() => {
        const cityOn = !nationwide && hasCityAnswer && !!cityClean;
        const local = cityOn && !!coords && !(matchInfo?.expanded && matchInfo.radiusKm == null);
        return <Ct2Heading count={local ? inRadiusCount : experts.length} plural={ct2Head.plural} singular={ct2Head.singular}
          cityName={local ? cityClean : null} as={ct2Head.tag} hasCity={cityOn} onChip={() => window.dispatchEvent(new Event('ct2:open-cities'))} />;
      })()}
      {!compactLine && !(ct2 && ct2Head && !campaignMode) && <div className={ct2 ? 'ct2-sh-row' : 'flex items-center justify-center flex-wrap gap-2 mb-4 leading-relaxed'}>
        {ct2 && <style>{CT2_SH_CSS}</style>}
        {campaignMode ? (
          <p className="text-base font-bold" style={{ color: '#0f172a' }}>{featuredIds.size === 1 ? `המומחה המומלץ שלנו ב${cityClean}` : `${featuredIds.size} המומחים המומלצים שלנו ב${cityClean}`}</p>
        ) : nationwide ? (
          <p className="text-base font-bold" style={{ color: '#0f172a' }}>{experts.length === 1 ? 'מומחה אחד — נותן שירות בכל הארץ' : `${experts.length} מומחים — כולם נותנים שירות בכל הארץ`}</p>
        ) : !hasCityAnswer ? (
          <p className="text-sm font-semibold" style={{ color: '#94a3b8' }}>ממתין לבחירת עיר</p>
        ) : !coords ? (
          <p className="text-sm font-semibold" style={{ color: '#94a3b8' }}>מאתר מומחים ב{cityClean}...</p>
        ) : matchInfo?.expanded ? (
          <p className="text-base font-bold" style={{ color: '#0f172a' }}>
            {matchInfo.radiusKm != null
              // 2026-08-23 (user request): no km disclosure to the visitor — same plain
              // format whether the radius quietly expanded or not, radiusKm/expanded are
              // still tracked internally (logCityDemandGap etc.), just never shown.
              ? (ct2 ? ct2Line(cityClean) : foundLine(cityClean))
              : `לא נמצאו מומחים קרובים ל${cityClean} — מציגים את כל המומחים בתחום`}
          </p>
        ) : (
          <p className="text-base font-bold" style={{ color: '#0f172a' }}>
            {ct2 ? ct2Line(cityClean) : foundLine(cityClean)}
          </p>
        )}
        {/* 2026-08-22 (user request): only when the city was AUTO-filled (no page-specific
            /{category}/{city} URL — that city is canonical to the page itself, resetting it
            here wouldn't do anything since it always wins over the store, see knownCity in
            QuickConsultBar) — lets a visitor whose city was guessed wrong pick a different
            one, and re-opens the bottom questionnaire's city question too (see
            lib/smartIntakeStore.ts's resetCity() + QuickConsultBar's city-reset listener). */}
        {/* 2026-09-28 (Founder): on a /{category}/{city} page too — the page city comes from the URL (the questionnaire skips the
            city question there), so the chip clears the city and opens the category page, which asks for a city again: the URL,
            the h1 and the list never disagree (no "Haifa experts under an Ashdod heading"). */}
        {!nationwide && hasCityAnswer && (!presetCity || categoryHref) && !(ct2 && ct2Head) && (
          <button type="button" onClick={() => { resetCity(); if (presetCity && categoryHref) window.location.href = categoryHref; }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold text-sm flex-shrink-0"
            style={{ background: '#f8fafc', color: '#64748b', border: '1.5px dashed #cbd5e1', cursor: 'pointer' }}>
            🔄 עיר אחרת
          </button>
        )}
      </div>}
      {sortBar && <div className="mb-3" data-sort-row>{sortBar}</div>}

      {compactCards && cardList ? (
        <div className={compactCards === 'tile' ? 'tp-car' : 'grid grid-cols-1 gap-2.5 md:grid-cols-2'} style={{ opacity: justResorted ? 0.45 : 1, transition: 'opacity .42s ease' }}>
          <style>{TOPIC_CARD_CSS}</style>
          {cardList.map((e, i) => (
            <div key={e.id} className="min-w-0"><ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
            <TopicProCard variant={compactCards} priority={i === 0}
              id={e.id} href={e.href} firstName={e.firstName} fullName={e.fullName} phone={e.phone} whatsappValid={e.whatsappValid} gender={e.gender}
              imgSrc={e.imgSrc} rating10={e.rating10} reviewCount={e.reviewCount} categorySlug={e.categorySlug} categoryName={e.categoryName}
              locationStr={e.locationStr} nationwide={nationwide} reviews={e.reviews as any}
              dimmed={campaignMode && !featuredIds.has(e.id)}
              dimNote={campaignMode && !featuredIds.has(e.id) ? `לא נותן שירות ב${cityClean}` : undefined} /></div>
          ))}
        </div>
      ) : (
      <div className="space-y-4" style={{ opacity: justResorted ? 0.45 : 1, transition: 'opacity .42s ease' }}>
        {visible.map((e, i) => (
          <div key={e.id}><ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
          <CategoryProCard proArea={null} ct2={ct2}
            priority={i === 0}
            id={e.id} href={e.href} firstName={e.firstName} fullName={e.fullName} phone={e.phone} whatsappValid={e.whatsappValid}
            imgSrc={e.imgSrc} rating10={e.rating10} reviewCount={e.reviewCount}
            categorySlug={e.categorySlug} categoryName={e.categoryName}
            locationStr={e.locationStr} availStatus={availStatus} avail={e.avail} reviews={e.reviews}
            nationwide={nationwide} gallery={e.gallery} galleryTags={e.galleryTags} proCity={e.proCity}
            dimmed={campaignMode && !featuredIds.has(e.id)}
            dimNote={campaignMode && !featuredIds.has(e.id) ? `לא נותן שירות ב${cityClean}` : undefined}
            distanceNote={!hideLocation && !nationwide && hasCityAnswer && cityClean ? proCityDistanceNote(e, cityClean, coords) : null}
          /></div>
        ))}
      </div>
      )}
      {hidden > 0 && compactCards !== 'tile' && (
        <div className="text-center mt-5">
          <button type="button" onClick={() => setExpanded(true)} className="inline-flex items-center justify-center font-bold rounded-full px-6" style={{ minHeight: 48, background: '#0d47a1', color: '#fff', fontSize: 15, cursor: 'pointer', border: 'none', boxShadow: '0 6px 18px rgba(13,71,161,.25)' }}><span>הצגת עוד מומחים (<bdi dir="ltr">{hidden}</bdi>)</span></button>
        </div>
      )}
    </div>
  );
}
